/// <reference path="../../types/window.d.ts" />

import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { debugTealium, debugTealiumError } from '../../utils/debug-logger';
import { useExternalScriptReady } from '../../utils/external-script';
import { useQueueUntilReady } from '../../utils/queue';

import './tealium-invariants';

/**
 * Tealium tracking provider that automatically queues events until Tealium loads.
 *
 * Responsibilities:
 * - Confirms Tealium data layer exists (`window.utag_data`)
 * - Confirms Tealium APIs exist (`window.utag.link`, `window.utag.view`)
 * - Queues events until Tealium is ready
 *
 * @example
 * <TealiumProvider><App /></TealiumProvider>
 *
 * const { fireLink, fireView } = useTealium()
 * fireLink(data) // user interactions
 * fireView(data) // page/route changes
 */

// #region JSDoc Types

/**
 * @typedef {typeof TEALIUM_COMMAND_TYPES[keyof typeof TEALIUM_COMMAND_TYPES]} TealiumCommandType
 */

/**
 * @typedef {typeof TEALIUM_SEND_RESULTS[keyof typeof TEALIUM_SEND_RESULTS]} TealiumSendResult
 * Result status indicating whether the event was sent immediately or queued
 */

/**
 * @typedef {Record<string, any>} TealiumCommandPayload
 * Event data object sent to Tealium.
 */

/**
 * @typedef {object} TealiumCommand
 * @property {TealiumCommandType} type
 * @property {TealiumCommandPayload} data
 */

/**
 * @typedef {(data: TealiumCommandPayload) => TealiumSendResult} TealiumSendFunction
 * Sends tracking data, automatically queuing if Tealium isn't ready.
 * @returns {TealiumSendResult} 'sent' if sent immediately, 'queued' if Tealium not ready
 * @see {@link TEALIUM_COMMAND_TYPES} for available command types
 */

/**
 * @typedef {object} TealiumContextValue
 * @property {TealiumSendFunction} fireLink For user interactions (clicks, form submissions, etc.)
 *  - internally calls {@link https://docs.tealium.com/platforms/javascript/api/tracking-functions/#utaglink utag.link}
 * @property {TealiumSendFunction} fireView For page views, virtual navigation, and page loads
 *  - internally calls {@link https://docs.tealium.com/platforms/javascript/api/tracking-functions/#utagview utag.view}
 */

// #endregion

// #region Constants and Context

/**
 * Tealium command type constants
 * @see {@link TealiumSendFunction} functions that use these types
 * @see {@link TealiumContextValue.fireLink} uses LINK
 * @see {@link TealiumContextValue.fireView} uses VIEW
 */
const TEALIUM_COMMAND_TYPES = /** @type {const} */ ({
  /** For user interactions (clicks, form submits, etc.) - used by fireLink */
  LINK: 'link',
  /** For page/route changes - used by fireView */
  VIEW: 'view',
});

/**
 * Tealium send result constants
 * @see {@link TealiumSendFunction} return values
 */
export const TEALIUM_SEND_RESULTS = /** @type {const} */ ({
  /** Event was sent immediately to Tealium */
  SENT: 'sent',
  /** Event was queued until Tealium loads */
  QUEUED: 'queued',
});

const TealiumContext = createContext(
  /** @type {TealiumContextValue | undefined} */ (undefined),
);

// #endregion

/**
 * Hook that returns `true` when Tealium script has loaded
 *
 * @returns {boolean}
 */
function useTealiumReady() {
  const checkIfTealiumReady = () =>
    typeof window !== 'undefined' &&
    typeof window.utag?.link === 'function' &&
    typeof window.utag?.view === 'function';

  return useExternalScriptReady(checkIfTealiumReady, {
    eventToListenFor: 'tealium_loaded',
    intervalMs: 500,
    maxDurationS: 60,
    onReady: () => {
      debugTealium(() => {
        console.groupCollapsed(
          '🟢 [Tealium] Ready (utag.link + utag.view) — click to expand for debugging tips',
        );
        console.log(
          '📖 For comprehensive Tealium debugging, see: https://docs.tealium.com/platforms/javascript/debugging/',
        );
        console.log('🍪 Enable Tealium debug mode by running this in console:');
        console.log('   document.cookie="utagdb=true";');
        console.log(
          '✨ This will show detailed tracking data and console output for all Tealium calls',
        );
        console.groupEnd();
      });
    },
    onTimeout: (duration) => {
      const readiness = {
        'has utag.link':
          typeof window !== 'undefined' &&
          typeof window.utag?.link === 'function',
        'has utag.view':
          typeof window !== 'undefined' &&
          typeof window.utag?.view === 'function',
      };
      debugTealiumError(() =>
        console.error(
          `❌ [Tealium] script failed to load after ${duration}s`,
          readiness,
        ),
      );
    },
  });
}

/**
 * Sends command to appropriate Tealium function.
 * @param {TealiumCommand} command
 */
function sendDataToTealium({ type, data }) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    debugTealium(() =>
      console.log(`📡 [Tealium] Calling \`utag.${type}()\``, data),
    );

    // Defer tracking to next tick to avoid blocking paint (improves INP for Core Web Vitals)
    setTimeout(() => {
      if (type === TEALIUM_COMMAND_TYPES.LINK && window.utag?.link) {
        // https://docs.tealium.com/platforms/javascript/api/tracking-functions/#utaglink
        window.utag.link(data);
      } else if (type === TEALIUM_COMMAND_TYPES.VIEW && window.utag?.view) {
        // https://docs.tealium.com/platforms/javascript/api/tracking-functions/#utagview
        window.utag.view(data);
      } else {
        debugTealiumError(() =>
          console.error(`❌ [Tealium] ${type} call undefined:`, { data }),
        );
      }
    }, 0);
  } catch (error) {
    debugTealiumError(() =>
      console.error(`❌ [Tealium] ${type} call failed:`, { error, data }),
    );

    // intentionally swallow errors to avoid breaking the site
  }
}

/**
 * Provider that enables Tealium tracking throughout the app.
 * Events are automatically queued until Tealium is ready.
 *
 * @param {{ children: React.ReactNode }} props
 */
export const TealiumProvider = ({ children }) => {
  const tealiumReady = useTealiumReady();

  const addToQueue = useQueueUntilReady(
    tealiumReady,
    /**
     * Flushes queued commands
     * @param {TealiumCommand[]} commands
     */
    function handleFlushQueue(commands) {
      commands.forEach((command) => {
        sendDataToTealium(command);
        debugTealium(() =>
          console.log(
            `🎯 [Tealium] Queued \`utag.${command.type}()\` sent successfully`,
          ),
        );
      });
    },
  );

  /**
   * Wraps a send call with queuing logic if Tealium isn't ready yet.
   */
  const wrapTealiumSend = useCallback(
    /**
     * @param {TealiumCommandType} type
     * @returns {TealiumSendFunction}
     */
    (type) => (data) => {
      const command = { type, data };

      if (tealiumReady) {
        sendDataToTealium(command);
        return TEALIUM_SEND_RESULTS.SENT;
      }

      debugTealium(() =>
        console.log(`⏳ [Tealium] Queued \`utag.${command.type}()\``, command),
      );
      addToQueue(command);
      return TEALIUM_SEND_RESULTS.QUEUED;
    },
    [tealiumReady, addToQueue],
  );

  /** @type {TealiumContextValue} */
  const contextValue = useMemo(
    () => ({
      fireLink: wrapTealiumSend(TEALIUM_COMMAND_TYPES.LINK),
      fireView: wrapTealiumSend(TEALIUM_COMMAND_TYPES.VIEW),
    }),
    [wrapTealiumSend],
  );

  return (
    <TealiumContext.Provider value={contextValue}>
      {children}
    </TealiumContext.Provider>
  );
};

/**
 * Access Tealium functions from context
 * @returns {TealiumContextValue}
 */
export const useTealium = () => {
  const context = useContext(TealiumContext);

  if (!context) {
    throw new Error('❌ useTealium must be used within a TealiumProvider');
  }

  return context;
};
